import { useEffect, useState } from "react";
import TaxiIcon from "./TaxiIcon";
import {
  clearPwaInstalled,
  getDeferredPrompt,
  promptPwaInstall,
  rememberPwaInstalled,
  subscribePwaInstall,
  wasPwaInstalled,
} from "./pwaInstall";
import { isTelegramMiniApp, isTelegramWebView } from "./telegramMiniApp";

// The app is PWA-only: in a browser tab the user sees an install guide instead
// of the application itself. Installation is platform-specific: Android Chrome
// mints a WebAPK through Play services, iOS Safari uses Share → Add to Home
// Screen, and desktop browsers use their own install menu.
const isStandalone = () => {
  if (typeof window === "undefined") return true;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.matchMedia?.("(display-mode: fullscreen)")?.matches ||
    window.navigator.standalone === true
  );
};

const detectPlatform = () => {
  const ua = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const ios =
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator?.platform === "MacIntel" && navigator?.maxTouchPoints > 1);
  const safari = ios && !/crios|fxios|edgios|opios/i.test(ua);
  const android = /android/i.test(ua);
  const chromeAndroid =
    android && /chrome|crios/i.test(ua) && !/samsungbrowser|opr|edga/i.test(ua);
  return { ios, safari, android, chromeAndroid, desktop: !ios && !android };
};

const InstallStep = ({ number, children }) => (
  <div className="install-step">
    <span className="install-step-number">{number}</span>
    <span>{children}</span>
  </div>
);

const InstallGuide = ({ state }) => {
  const { platform } = state;

  if (platform.ios && platform.safari) {
    return (
      <div className="install-guide">
        <InstallStep number="1">Нажмите «Поделиться»</InstallStep>
        <InstallStep number="2">Выберите «На экран „Домой“»</InstallStep>
        <InstallStep number="3">Нажмите «Добавить»</InstallStep>
      </div>
    );
  }

  if (platform.ios) {
    return (
      <div className="install-guide">
        <div className="install-guide-lead">Откройте сайт в Safari</div>
        <button type="button" className="install-guide-action" onClick={state.copyLink}>
          Скопировать ссылку
        </button>
      </div>
    );
  }

  if (platform.android) {
    // Telegram's in-app browser cannot mint a WebAPK: send the user out to
    // Chrome, where the regular install flow works
    if (state.telegramWebView) {
      return (
        <div className="install-guide">
          <div className="install-guide-lead">
            Откройте в Chrome: меню ⋮ → «Открыть в браузере»
          </div>
          <button type="button" className="install-guide-action" onClick={state.copyLink}>
            Скопировать ссылку
          </button>
        </div>
      );
    }
    return (
      <div className="install-guide">
        <div className="install-guide-lead">
          {platform.chromeAndroid ? "Chrome ⋮ → «Установить приложение»" : "Откройте сайт в Chrome"}
        </div>
        {platform.chromeAndroid && (
          <div className="install-guide-small">Нет пункта — включите Google Play</div>
        )}
        <button type="button" className="install-guide-action" onClick={state.retry}>
          Проверить снова
        </button>
      </div>
    );
  }

  return (
    <div className="install-guide">
      <div className="install-guide-lead">Chrome/Edge ⋮ → «Установить приложение»</div>
    </div>
  );
};

export const usePwaState = () => {
  const [standalone, setStandalone] = useState(isStandalone);
  const [promptAvailable, setPromptAvailable] = useState(Boolean(getDeferredPrompt()));
  const [installed, setInstalled] = useState(wasPwaInstalled);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);
  const [platform] = useState(detectPlatform);
  const [guide, setGuide] = useState(platform.ios);

  useEffect(() => {
    const query = window.matchMedia?.("(display-mode: standalone)");
    const onChange = () => setStandalone(isStandalone());
    query?.addEventListener?.("change", onChange);

    let cancelled = false;
    // Chrome on Android reports the real install state: deleting the WebAPK
    // icon leaves the localStorage flag behind, so trust the system answer
    // over the flag in both directions. Where the API is missing (iOS),
    // the flag stays the only signal.
    navigator.getInstalledRelatedApps?.().then((apps) => {
      if (cancelled || !apps) return;
      if (apps.some((app) => app.platform === "webapp")) {
        rememberPwaInstalled();
        setInstalled(true);
        return;
      }
      if (!isStandalone() && wasPwaInstalled()) {
        clearPwaInstalled();
        setInstalled(false);
      }
    }).catch(() => undefined);

    const unsubscribe = subscribePwaInstall(() => {
      setPromptAvailable(Boolean(getDeferredPrompt()));
      if (wasPwaInstalled()) setInstalled(true);
    });
    const onInstalled = () => {
      setInstalled(true);
      setGuide(false);
      setStandalone(isStandalone());
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      cancelled = true;
      query?.removeEventListener?.("change", onChange);
      unsubscribe();
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    setHint(null);
    if (getDeferredPrompt()) {
      setBusy(true);
      const result = await promptPwaInstall();
      setPromptAvailable(Boolean(getDeferredPrompt()));
      setBusy(false);
      if (result.outcome === "accepted") setHint("Устанавливаем приложение…");
      else if (result.outcome === "dismissed") {
        setHint("Установка отменена. Попробуйте ещё раз.");
        setGuide(true);
      } else {
        setHint("Chrome не открыл установку. Проверьте Google Play и повторите.");
        setGuide(true);
      }
      return;
    }
    setGuide(true);
  };

  const retry = () => {
    setHint(null);
    setGuide(false);
    setPromptAvailable(Boolean(getDeferredPrompt()));
    window.location.reload();
  };

  const copyLink = async () => {
    const browser = platform.ios ? "Safari" : "Chrome";
    try {
      await navigator.clipboard.writeText(window.location.href);
      setHint(`Ссылка скопирована. Откройте её в ${browser}.`);
    } catch (error) {
      setHint(`Откройте в ${browser}: ${window.location.href}`);
    }
  };

  return {
    standalone,
    installed,
    busy,
    hint,
    guide,
    platform,
    promptAvailable,
    telegramWebView: isTelegramWebView(),
    canInstall: promptAvailable,
    install,
    retry,
    copyLink,
  };
};

export const InstallGate = ({ state, children }) => {
  // A Telegram Mini App needs no installation: the WebView is the container
  if (state.standalone || state.installed || isTelegramMiniApp()) return children;

  const buttonLabel = state.busy
    ? "Устанавливаем…"
    : state.promptAvailable
      ? "Установить"
      : "Как установить";

  return (
    <div className="install-gate">
      <div className="install-gate-card install-gate-compact">
        <div className="install-gate-logo">
          <img src="/icons/icon-192.png" alt="Бомбила" width="88" height="88" />
        </div>
        <div className="brand-title brand-gradient install-gate-title">Бомбила</div>

        {state.promptAvailable ? (
          <button
            type="button"
            className="press btn-accent install-gate-button"
            onClick={state.install}
            disabled={state.busy}
          >
            <TaxiIcon name="down" size={20} />
            {buttonLabel}
          </button>
        ) : state.platform.ios ? null : (
          <button
            type="button"
            className="press btn-accent install-gate-button"
            onClick={state.install}
            disabled={state.busy}
          >
            <TaxiIcon name="down" size={20} />
            {buttonLabel}
          </button>
        )}

        {state.hint && <div className="install-gate-hint">{state.hint}</div>}
        {state.guide && <InstallGuide state={state} />}
      </div>
    </div>
  );
};

export const PwaGate = ({ children }) => {
  const state = usePwaState();
  return <InstallGate state={state}>{children}</InstallGate>;
};

export default PwaGate;
